import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useQueryClient, useMutation } from "react-query";
import styled from "styled-components";
import * as Yup from "yup";

import useCurrentUser from "../../hooks/useCurrentUser";
import useHelper from "../../hooks/useHelper";
import useLeagueSeason from "../../hooks/queryResults/useLeagueSeason";
import useTeams from "../../hooks/queryResults/useTeams";
import useUrls from "../../hooks/useUrls";
import http from "../../services/httpService";
import Util from "../../util/util";
import ButtonSubmit from "../form/ButtonSubmit";
import TextInput from "../form/TextInput";
import TeamLogoAndName from "../team/TeamLogoAndName";
import Dropdown from "../ui/Dropdown";
import Loading from "../ui/Loading";

const StyledDivContainer = styled.div`
  & .err {
    width: 100%;
    max-width: 100%;
  }
`;

export default function MessagePostForm({
  handleHideForm,
  replyToMessageId,
  isReply,
}) {
  console.log(`replying to message: ${replyToMessageId}`);
  const helper = useHelper();
  const { teams, teamsQuery } = useTeams();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { league, leagueSeason, leagueQuery } = useLeagueSeason();
  const [errorMessage, setErrorMessage] = useState("");
  const { apiUrl } = useUrls();

  const mutation = useMutation(
    (data) => {
      const url = `${apiUrl}/leagues/${league.getIdOrSlug()}/${
        leagueSeason.obj.season
      }/message-board`;

      if (data.fromTeamId === "commish") {
        data.fromTeamId = "";
        data.asCommish = true;
      } else {
        data.asCommish = false;
      }

      const config = Util.addTokenToHeaders();
      console.log(`sending request to url ${url} with data:`, data);

      return http.post(url, data, config);
    },
    {
      onSuccess: async () => {
        console.log("add message board post - mutation success");
        await queryClient.invalidateQueries([
          "league/message-board",
          leagueSeason?.obj?.id,
        ]);
      },
      onError: (error) => {
        setErrorMessage(error.response.data.error);
      },
    }
  );

  const loaded = teams && league && leagueSeason;
  const isLoading = teamsQuery.isLoading || leagueQuery.isLoading;

  if (isLoading) return <Loading />;

  let fromTeamId = "";
  let teamItems = [{ id: 1, value: "" }];
  if (league && teams) {
    const userTeams = helper.getTeamsForUser(currentUser, teams);
    teamItems = userTeams
      .sort((a, b) => {
        if (a.fullName === b.fullName) return 0;
        return a.fullName < b.fullName ? -1 : 1;
      })
      .map((team) => ({
        id: team?.obj?.id,
        value: (
          <div style={{ display: "flex", alignItems: "center" }}>
            <TeamLogoAndName
              team={team}
              week={null}
              size={"sm"}
              linkPath={null}
            />
            <div>({currentUser.fullName})</div>
          </div>
        ),
      }));

    if (league.hasRoleCommish(currentUser.obj.username)) {
      teamItems.push({
        id: "commish",
        value: `Commissioner (${currentUser.fullName})`,
      });
    }

    if (!fromTeamId && teamItems.length) fromTeamId = teamItems[0].id;
  }

  if (!loaded) {
    handleHideForm();
    return "information not loaded.";
  }

  if (mutation.isSuccess) {
    handleHideForm();
    return null;
  }

  const title = isReply ? "Reply to Thread" : "Add New Post";

  return (
    <StyledDivContainer
      style={{ background: "#3B9AFF", marginBottom: "1rem", padding: "1rem" }}
    >
      {mutation.isLoading ? (
        <span>Posting message...</span>
      ) : (
        <Formik
          initialValues={{
            subject: mutation.variables?.subject || "",
            content: mutation.variables?.content || "",
            fromTeamId: mutation.variables?.fromTeamId || fromTeamId,
            replyToMessageId:
              mutation.variables?.replyToMessageId || replyToMessageId,
            asCommish: mutation.variables?.asCommish || false,
          }}
          validateOnMount={true}
          validationSchema={Yup.object({
            subject: Yup.string(),
            content: Yup.string(),
            fromTeamId: Yup.string().nullable(true),
            replyToMessageId: Yup.string().nullable(true),
            asCommish: Yup.boolean().default(false).required(),
          })}
          onSubmit={mutation.mutate}
        >
          {({ isValid, setFieldValue, values }) => (
            <>
              {mutation.isError ? (
                <div className={"alert"}>{errorMessage}</div>
              ) : null}
              {
                <Form style={{ padding: 0, color: "white" }}>
                  <h2 style={{ marginTop: 0 }}>{title}</h2>
                  Posting as:{" "}
                  <div style={{ marginBottom: "1rem" }}>
                    <Dropdown
                      items={teamItems}
                      selectedId={values.fromTeamId}
                      handleSelectItem={(item) => {
                        setFieldValue("fromTeamId", item.id);
                        fromTeamId = item.id;
                      }}
                    />
                  </div>
                  <Field type={"hidden"} name={"fromTeamId"} />
                  <Field type={"hidden"} name={"replyToMessageId"} />
                  <TextInput
                    name={"subject"}
                    type={"text"}
                    label={"Subject"}
                    style={{ width: "100%", minWidth: "100%" }}
                  />
                  <TextInput
                    name={"content"}
                    as={"textarea"}
                    label={"Content"}
                  />
                  <div style={{ display: "flex" }}>
                    <ButtonSubmit label={title} formIsValid={isValid} />
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleHideForm();
                      }}
                      className={"btn btn-cancel"}
                    >
                      Cancel
                    </button>
                  </div>
                </Form>
              }
            </>
          )}
        </Formik>
      )}
    </StyledDivContainer>
  );
}
